import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { MenuOptionValue, NestedPick, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Maintenance, MaintenanceNoCarDependency, UpsertMaintenance } from "./types";

export const useMaintenancesApi = () => {
  const api = useApi();

  const getAdminMaintenance = useCallback(
    (maintenanceId: string, signal: AbortSignal) => {
      const url = `/admin/maintenances/${maintenanceId}`;
      return useSWRMutation<Maintenance | null, CustomError, string>(url, (url) =>
        api.get<Maintenance>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminMaintenance = useCallback(
    <K extends keyof MaintenanceNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<MaintenanceNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<MaintenanceNoCarDependency, K>>(
        carId ? `/admin/cars/${carId}/maintenances` : "/admin/maintenances",
        query,
      );
      return useSWR<Paginated<NestedPick<MaintenanceNoCarDependency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<MaintenanceNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminMaintenance = useCallback(
    async (body: UpsertMaintenance): Promise<{ id: string }> => {
      const url = `/admin/maintenances`;
      const id = await api.post<UpsertMaintenance, { id: string }>(url, body);
      api.mutateCache([url, `/admin/cars/${body.carId}/maintenances`]);
      return id;
    },
    [api],
  );

  const updateAdminMaintenance = useCallback(
    async (id: string, body: UpsertMaintenance) => {
      const url = `/admin/maintenances/${id}`;
      await api.put<UpsertMaintenance>(url, body);
      api.mutateCache([`/admin/maintenances`, `/admin/cars/${body.carId}/maintenances`]);
    },
    [api],
  );

  const deleteAdminMaintenance = useCallback(
    async (maintenanceId: string, carId: string): Promise<void> => {
      const url = `/admin/maintenances/${maintenanceId}`;
      await api.del(url);
      api.mutateCache([`/admin/maintenances`, `/admin/cars/${carId}/maintenances`]);
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/maintenances/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminMaintenance,
    updateAdminMaintenance,
    insertAdminMaintenance,
    useAdminMaintenance,
    getAdminMaintenance,
    getFieldFilters,
  };
};
